import { Dictionary, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';
import * as fromActions from './quicklinks-sets-table.actions';

import { Pagination, QuicklinksSet } from '@app/core/models';
import { TableState, flattenFilters } from '@app/shared/filters/filters-model';
import { INITIAL_QL_SETS_FILTERS, ManageSetsTableFilters } from '@app/shared/filters/models/manage-sets-table';

export const feature = 'quicklinksSetsTable';

export const adapter: EntityAdapter<QuicklinksSet> =
 createEntityAdapter<QuicklinksSet>({
  selectId: (e) => e.id
 });

export interface State {
  table: TableState<QuicklinksSet, ManageSetsTableFilters>;
}

export const initialState: State = {
  table: {
    data: adapter.getInitialState(),
    pagination: {
      ...Pagination,
      pageIndex: 0,
      pageSize: 10,
    },
    sort: {
      active: 'name',
      direction: 'asc'
    },
    defaultSort: {
      active: 'name',
      direction: 'asc'
    },
    filters: INITIAL_QL_SETS_FILTERS,
    defaultFilters: INITIAL_QL_SETS_FILTERS,
    isLoading: false,
    resetIndex: false,
  },
};

const tableReducer = createReducer(
  initialState,
  on(fromActions.getTableRequest, (state) => ({
    ...state,
    table: {
      ...state.table,
      isLoading: true
    }
  })),
  on(fromActions.getTableSuccess, fromActions.getTableFailure, (state) => ({
    ...state,
    table: {
      ...state.table,
      isLoading: false
    }
  })),
  on(fromActions.getTableSuccess, (state, {data, pagination}) => ({
    ...state,
    table: {
      ...state.table,
      data: adapter.setAll(data, state.table.data),
      pagination,
      resetIndex: false,
    }
  })),
  on(fromActions.changeTableFilters, (state, {filters}) => ({
    ...state,
    table: {
      ...state.table,
      filters,
      resetIndex: true
    }
  })),
  on(fromActions.changeTableSort, (state, {active, direction}) => ({
    ...state,
    table: {
      ...state.table,
      sort: {
        active,
        direction
      }
    }
  })),
  on(fromActions.changeTablePagination, (state, {pageIndex, pageSize}) => ({
    ...state,
    table: {
      ...state.table,
      pagination: {
        ...state.table.pagination,
        pageIndex,
        pageSize
      }
    }
  })),
  on(fromActions.deleteQuicklinksSetSuccess, (state) => ({
    ...state,
    table: {
      ...state.table,
      resetIndex: true
    }
  })),
  on(fromActions.clearAll, (state) => ({
    ...initialState
  })),

);

export function reducer(state: State | undefined, action: Action) {
  return tableReducer(state, action);
}

// NEW WAY TABLE
export const getTableState = (state: State) => state.table;

export const getPagination = (table: TableState<QuicklinksSet, ManageSetsTableFilters>) => table.pagination;
export const getSort = (table: TableState<QuicklinksSet, ManageSetsTableFilters>) => table.sort;
export const getDefaultSort = (table: TableState<QuicklinksSet, ManageSetsTableFilters>) => table.defaultSort;
export const getFilters = (table: TableState<QuicklinksSet, ManageSetsTableFilters>) => table.filters;
export const getDefaultFilters = (table: TableState<QuicklinksSet, ManageSetsTableFilters>) => table.defaultFilters;
export const getIsLoading = (table: TableState<QuicklinksSet, ManageSetsTableFilters>) => table.isLoading;

export const getRequestData = ({pagination, sort, filters, resetIndex}: TableState<QuicklinksSet, ManageSetsTableFilters>) => ({
  pageIndex: pagination.pageIndex,
  pageSize: pagination.pageSize,
  sort,
  filters: flattenFilters(filters),
  resetIndex
});

export const getTableData = (table: TableState<QuicklinksSet, ManageSetsTableFilters>) => adapter.getSelectors().selectAll(table.data);

// Pagination fields
export const getPageSize = (pagination: Pagination) => pagination.pageSize;
export const getPageIndex = (pagination: Pagination) => pagination.pageIndex;
export const getTotalCount = (pagination: Pagination) => pagination.totalCount;

// Filters fields
export const getSearch = (filters: ManageSetsTableFilters) => filters.search.value;

export const getEntitiesByIds = (entities: Dictionary<QuicklinksSet>, ids: string[]): QuicklinksSet[] => ids.map(id => entities[id]);
